import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import TodayIcon from '@mui/icons-material/Today'
import {
  Box,
  CircularProgress,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material'
import { Button } from 'ui/baseComponents'
import PropTypes from 'prop-types'
import { useQuery } from '@apollo/client'
import { useMemo, useRef, useState } from 'react'
import { GET_OUTREACH_DELIVERY_FORECAST } from '../graphql/campaignQueries'
import { getRemovedStepLabel } from '../stepLabels'

const SLIDE_STYLE = `
  @keyframes slideInFromRight {
    from { transform: translateX(40px); opacity: 0; }
    to   { transform: translateX(0);    opacity: 1; }
  }
  @keyframes slideInFromLeft {
    from { transform: translateX(-40px); opacity: 0; }
    to   { transform: translateX(0);     opacity: 1; }
  }
`

const WINDOW_SIZE = 15
const STEP = 7
// Fetch granularity: round up to this boundary and add one extra step as buffer
const FETCH_STEP = 14

const pad = (n) => String(n).padStart(2, '0')
const toISO = (d) =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`

const offsetDate = (isoToday, days) => {
  const [y, m, d] = isoToday.split('-').map(Number)
  const date = new Date(y, m - 1, d)
  date.setDate(date.getDate() + days)
  return toISO(date)
}

const PAST_COLORS = [
  [0, '#f5f5f5'],
  [0.001, '#e0e0e0'],
  [0.5, '#9e9e9e'],
  [1, '#424242'],
]

const FORECAST_COLORS = [
  [0, '#f5f5f5'],
  [0.001, '#f0f0f0'],
  [0.5, '#42a5f5'],
  [1, '#0d47a1'],
]

const contrastColor = (intensity) => (intensity > 0.5 ? '#ffffff' : '#212121')

const formatColHeader = (dateStr, todayStr) => {
  const [year, month, day] = dateStr.split('-').map(Number)
  const date = new Date(year, month - 1, day)
  const weekday = date.toLocaleDateString('en-US', { weekday: 'short' })
  return { weekday, short: `${month}/${day}`, isToday: dateStr === todayStr }
}

const interpolateColor = (hex1, hex2, t) => {
  const parse = (h) => [
    parseInt(h.slice(1, 3), 16),
    parseInt(h.slice(3, 5), 16),
    parseInt(h.slice(5, 7), 16),
  ]
  const [r1, g1, b1] = parse(hex1)
  const [r2, g2, b2] = parse(hex2)
  return `rgb(${Math.round(r1 + (r2 - r1) * t)},${Math.round(
    g1 + (g2 - g1) * t,
  )},${Math.round(b1 + (b2 - b1) * t)})`
}

const colorFromScale = (scale, t) => {
  if (t <= 0) return scale[0][1]
  if (t >= 1) return scale[scale.length - 1][1]
  for (let i = 1; i < scale.length; i++) {
    if (t <= scale[i][0]) {
      const prev = scale[i - 1]
      const curr = scale[i]
      return interpolateColor(
        prev[1],
        curr[1],
        (t - prev[0]) / (curr[0] - prev[0]),
      )
    }
  }
  return scale[scale.length - 1][1]
}

// Derive query variables from windowOffset, rounding to FETCH_STEP boundaries
// so that navigation within a step reuses the same cached query.
const queryVarsForOffset = (windowOffset) => {
  const neededLookback = Math.max(0, -windowOffset)
  const neededLookahead = Math.max(0, windowOffset + WINDOW_SIZE - 1)
  const lookbackDays =
    Math.ceil(neededLookback / FETCH_STEP) * FETCH_STEP + FETCH_STEP
  const lookaheadDays =
    Math.ceil(neededLookahead / FETCH_STEP) * FETCH_STEP + FETCH_STEP
  return { lookbackDays, lookaheadDays }
}

/**
 * Delivery forecast heatmap with navigation and click-to-filter.
 * @param {object} props - Component props
 * @param {string} props.campaignId - Campaign to forecast for
 * @param {Array} props.campaignContent - Sorted campaign content steps from the campaign query
 * @param {function} props.onSelectDate - Called with ISO date string or null
 * @param {function} props.onSelectCell - Called with campaignContentId or null
 * @param {string|null} props.selectedDate - Currently filtered date
 * @param {string|null} props.selectedContentId - Currently filtered content id
 * @returns {React.ReactElement|null} Rendered heatmap or null
 */
const DeliveryForecastHeatmap = ({
  campaignId,
  campaignContent,
  onSelectDate,
  onSelectCell,
  selectedDate,
  selectedContentId,
}) => {
  // windowOffset: days from today to the start of the 14-day visible window
  const [windowOffset, setWindowOffset] = useState(-7)
  const [slideDir, setSlideDir] = useState(null)
  const animKey = useRef(0)

  const handleBack = () => {
    setSlideDir('left')
    animKey.current += 1
    setWindowOffset((o) => o - STEP)
  }

  const handleForward = () => {
    setSlideDir('right')
    animKey.current += 1
    setWindowOffset((o) => o + STEP)
  }

  const { lookbackDays, lookaheadDays } = queryVarsForOffset(windowOffset)

  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone

  const { data, loading } = useQuery(GET_OUTREACH_DELIVERY_FORECAST, {
    variables: { campaignId, lookbackDays, lookaheadDays, timezone },
    fetchPolicy: 'cache-first',
    skip: !campaignId,
  })
  const forecast = data?.outreachDeliveryForecast

  const { rows, grid, rowMaxes } = useMemo(() => {
    const rowMap = new Map(
      (campaignContent ?? []).map((c) => [
        c.id,
        {
          campaignContentId: c.id,
          templateName: c.template?.name ?? c.id,
          sequence: c.sequence,
        },
      ]),
    )

    if (forecast) {
      forecast.days.forEach((day) => {
        day.contentSteps.forEach((step) => {
          if (!rowMap.has(step.campaignContentId)) {
            rowMap.set(step.campaignContentId, {
              campaignContentId: step.campaignContentId,
              templateName: step.isRemoved
                ? getRemovedStepLabel(step.templateName, step.campaignContentId)
                : step.templateName,
              sequence: step.sequence,
            })
          }
        })
      })
    }

    // Retired steps have a null sequence; keep them after the active steps.
    const rows = [...rowMap.values()].sort((a, b) => {
      const aRetired = a.sequence === null || a.sequence === undefined
      const bRetired = b.sequence === null || b.sequence === undefined
      if (aRetired && bRetired) return 0
      if (aRetired) return 1
      if (bRetired) return -1
      return a.sequence - b.sequence
    })
    const grid = {}
    const rowMaxes = {}
    rows.forEach((row) => {
      rowMaxes[row.campaignContentId] = 0
    })

    if (forecast) {
      forecast.days.forEach((day) => {
        day.contentSteps.forEach((step) => {
          const key = `${step.campaignContentId}__${day.date}`
          grid[key] = {
            patientCount: step.patientCount,
            isPast: day.isPast,
            templateName: step.templateName,
            sequence: step.sequence,
            campaignContentId: step.campaignContentId,
            date: day.date,
          }
          if (step.patientCount > (rowMaxes[step.campaignContentId] ?? 0)) {
            rowMaxes[step.campaignContentId] = step.patientCount
          }
        })
      })
    }

    return { rows, grid, rowMaxes }
  }, [forecast, campaignContent])

  const COL_WIDTH = 56
  const COL_GAP = 10
  const ROW_HEIGHT = 36
  const ROW_GAP = 4
  const HEADER_HEIGHT = 40
  const LABEL_WIDTH = 140

  const rowCount = (campaignContent ?? []).length
  const placeholderHeight =
    HEADER_HEIGHT + ROW_GAP + rowCount * (ROW_HEIGHT + ROW_GAP)

  if (loading && !forecast) {
    return (
      <Box
        sx={{
          height: placeholderHeight,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CircularProgress size={24} />
      </Box>
    )
  }
  if (!forecast) return null

  const { today } = forecast

  // Build the 14 visible date strings from today + windowOffset
  const visibleDates = Array.from({ length: WINDOW_SIZE }, (_, i) =>
    offsetDate(today, windowOffset + i),
  )

  const visibleCols = visibleDates.map((date) => {
    const day = forecast.days.find((d) => d.date === date)
    return { date, isPast: day?.isPast ?? false }
  })

  const handleColClick = (date, isPast) => {
    if (!isPast) return
    if (selectedDate === date && !selectedContentId) {
      onSelectDate(null)
    } else {
      onSelectDate(date)
      onSelectCell(null)
    }
  }

  const handleCellClick = (cell) => {
    if (!cell?.isPast) return
    const isSame =
      selectedDate === cell.date && selectedContentId === cell.campaignContentId
    if (isSame) {
      onSelectCell(null)
      onSelectDate(null)
    } else {
      onSelectDate(cell.date)
      onSelectCell(cell.campaignContentId)
    }
  }

  return (
    <Box>
      <style>{SLIDE_STYLE}</style>
      <Box sx={{ overflowX: 'auto' }}>
        <Box sx={{ display: 'flex', minWidth: 'max-content' }}>
          {/* Row labels — in-flow so scroll container accounts for their width */}
          <Box
            sx={{
              width: LABEL_WIDTH,
              flexShrink: 0,
              pt: '6px',
            }}
          >
            <Box sx={{ height: HEADER_HEIGHT + ROW_GAP }} />
            {rows.map((row, i) => (
              <Box
                key={row.campaignContentId}
                sx={{
                  height: ROW_HEIGHT,
                  mb: i < rows.length - 1 ? `${ROW_GAP}px` : 0,
                  pr: 1,
                  overflow: 'hidden',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <Typography
                  variant="caption"
                  noWrap
                  title={row.templateName}
                  sx={{
                    display: 'block',
                    color: 'text.secondary',
                    width: '100%',
                  }}
                >
                  <Box
                    component="span"
                    sx={{ color: 'text.disabled', mr: 0.5 }}
                  >
                    {row.sequence}.
                  </Box>
                  {row.templateName}
                </Typography>
              </Box>
            ))}
          </Box>

          {/* Nav + columns */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              overflow: 'visible',
              alignItems: 'center',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                overflow: 'visible',
              }}
            >
              <IconButton onClick={handleBack}>
                <ChevronLeftIcon />
              </IconButton>

              <Box sx={{ pb: '6px', pt: '6px' }}>
                <Box
                  key={animKey.current}
                  sx={{
                    display: 'flex',
                    gap: `${COL_GAP}px`,
                    animation: slideDir
                      ? `${
                          slideDir === 'right'
                            ? 'slideInFromRight'
                            : 'slideInFromLeft'
                        } 0.2s ease-out`
                      : 'none',
                  }}
                >
                  {/* Columns */}
                  {visibleCols.map(({ date, isPast }) => {
                    const { weekday, short, isToday } = formatColHeader(
                      date,
                      today,
                    )
                    const isColSelected =
                      selectedDate === date && !selectedContentId

                    return (
                      <Box
                        key={date}
                        onClick={() => handleColClick(date, isPast)}
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: `${ROW_GAP}px`,
                          flexShrink: 0,
                          cursor: isPast ? 'pointer' : 'default',
                          px: '3px',
                          pb: '3px',
                          mx: '-3px',
                          bgcolor: isColSelected
                            ? '#ffecb3'
                            : isToday
                            ? '#f0f0f0'
                            : 'transparent',
                          '&:hover': isPast ? { opacity: 0.85 } : null,
                        }}
                      >
                        <Box
                          sx={{
                            width: COL_WIDTH,
                            height: HEADER_HEIGHT,
                            textAlign: 'center',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                          }}
                        >
                          <Typography
                            variant="caption"
                            display="block"
                            sx={{
                              fontWeight: isToday ? 700 : 400,
                              color: isToday
                                ? 'text.primary'
                                : 'text.secondary',
                              lineHeight: 1.2,
                            }}
                          >
                            {weekday}
                          </Typography>
                          <Typography
                            variant="caption"
                            display="block"
                            sx={{
                              fontWeight: isToday ? 700 : 400,
                              color: isToday
                                ? 'text.primary'
                                : 'text.secondary',
                              lineHeight: 1.2,
                            }}
                          >
                            {short}
                          </Typography>
                        </Box>

                        {rows.map((row) => {
                          const rowMax = rowMaxes[row.campaignContentId] || 1
                          const cell = grid[`${row.campaignContentId}__${date}`]
                          const count = cell?.patientCount ?? 0
                          const intensity = count > 0 ? count / rowMax : 0
                          const scale = isPast ? PAST_COLORS : FORECAST_COLORS
                          const emptyBg = '#f5f5f5'
                          const bg =
                            count > 0
                              ? colorFromScale(scale, intensity)
                              : emptyBg
                          const textColor =
                            count > 0 ? contrastColor(intensity) : '#bdbdbd'
                          const isSelected =
                            selectedDate === date &&
                            selectedContentId === row.campaignContentId

                          const tooltipTitle =
                            count > 0 ? (
                              <Box>
                                <Typography variant="caption" display="block">
                                  {date}
                                </Typography>
                                <Typography variant="caption" display="block">
                                  {row.templateName}
                                </Typography>
                                <Typography variant="caption" display="block">
                                  Seq {row.sequence} &middot; {count} patients
                                </Typography>
                                <Typography variant="caption" display="block">
                                  {isPast ? 'Actual sends' : 'Forecast'}
                                </Typography>
                              </Box>
                            ) : (
                              ''
                            )

                          return (
                            <Tooltip
                              key={row.campaignContentId}
                              title={tooltipTitle}
                              arrow
                            >
                              <Box
                                onClick={(e) => {
                                  if (!cell?.isPast) return
                                  e.stopPropagation()
                                  handleCellClick(cell)
                                }}
                                sx={{
                                  width: COL_WIDTH,
                                  height: ROW_HEIGHT,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  bgcolor: isSelected ? '#ffd54f' : bg,
                                  cursor: cell?.isPast ? 'pointer' : 'default',
                                  transition: 'filter 0.1s',
                                }}
                              >
                                {count > 0 && (
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      color: textColor,
                                      fontWeight: 600,
                                      fontSize: 11,
                                      lineHeight: 1,
                                    }}
                                  >
                                    {count}
                                  </Typography>
                                )}
                              </Box>
                            </Tooltip>
                          )
                        })}
                      </Box>
                    )
                  })}
                </Box>
              </Box>

              <IconButton onClick={handleForward}>
                <ChevronRightIcon />
              </IconButton>
            </Box>

            {/* Legend */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                mt: 0.5,
                alignSelf: 'flex-start',
                pl: '44px',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: 0.25,
                    bgcolor: '#9e9e9e',
                  }}
                />
                <Typography variant="caption" color="text.secondary">
                  Historical
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: 0.25,
                    bgcolor: '#42a5f5',
                  }}
                />
                <Typography variant="caption" color="text.secondary">
                  Forecast
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: 0.25,
                    bgcolor: '#f0f0f0',
                  }}
                />
                <Typography variant="caption" color="text.secondary">
                  Today
                </Typography>
              </Box>
              <Button
                type="secondary"
                size="small"
                startIcon={<TodayIcon sx={{ fontSize: '14px !important' }} />}
                onClick={() => {
                  setSlideDir(windowOffset > -7 ? 'left' : 'right')
                  animKey.current += 1
                  setWindowOffset(-7)
                }}
                sx={{
                  visibility: windowOffset !== -7 ? 'visible' : 'hidden',
                  py: 0,
                  fontSize: 11,
                }}
              >
                Back to today
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

DeliveryForecastHeatmap.propTypes = {
  campaignId: PropTypes.string.isRequired,
  campaignContent: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      sequence: PropTypes.number.isRequired,
      template: PropTypes.shape({ name: PropTypes.string }),
    }),
  ),
  onSelectDate: PropTypes.func.isRequired,
  onSelectCell: PropTypes.func.isRequired,
  selectedDate: PropTypes.string,
  selectedContentId: PropTypes.string,
}

export default DeliveryForecastHeatmap
